import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useAdminTenants } from '../../../../contexts/AdminEventsProvider';
import { adminWoltService } from '../../../../services/admin-wolt/admin-wolt.service';
import { productsService, type ProductView } from '../../../../services/products/products.service';
import { notifyWoltMenuSync } from '../../../../utils/wolt-menu-sync-notify';

type UseWoltMenuSectionArgs = {
  tenantId: string | null;
  employeeSession: string | null;
};

// Mapping list state: manager-session product list + include toggles, super-admin menu push.
export function useWoltMenuSection({ tenantId, employeeSession }: UseWoltMenuSectionArgs) {
  const { tenants } = useAdminTenants();
  const tenant = tenants.find((entry) => entry.tenantId === tenantId) ?? null;
  const woltConnected = Boolean(tenant?.woltConnected);
  const canEdit = woltConnected && Boolean(employeeSession);

  const [products, setProducts] = useState<ProductView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [togglingIds, setTogglingIds] = useState<number[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const toggleLocksRef = useRef<Set<number>>(new Set());
  const [isLinking, setIsLinking] = useState(false);
  const syncLockRef = useRef(false);
  const linkLockRef = useRef(false);

  useEffect(() => {
    if (!canEdit || !tenantId || !employeeSession) {
      setProducts([]);
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    productsService
      .listForSettings(tenantId, employeeSession)
      .then((rows) => {
        if (active) setProducts(rows);
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'טעינת המוצרים נכשלה');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canEdit, tenantId, employeeSession, refreshTick]);

  const toggleIncluded = async (product: ProductView) => {
    if (!employeeSession || toggleLocksRef.current.has(product.id)) {
      return;
    }
    toggleLocksRef.current.add(product.id);
    setTogglingIds((current) => [...current, product.id]);
    try {
      await productsService.update(product.id, { woltMenuIncluded: !product.woltMenuIncluded }, employeeSession);
      setRefreshTick((tick) => tick + 1);
    } catch (mutateError: unknown) {
      notifications.show({ color: 'red', message: mutateError instanceof Error ? mutateError.message : 'עדכון המוצר נכשל', autoClose: 4000 });
    } finally {
      toggleLocksRef.current.delete(product.id);
      setTogglingIds((current) => current.filter((id) => id !== product.id));
    }
  };

  // Opens Wolt's self-service portal in a new tab; the connected state arrives via tenant SSE.
  const connectToWolt = async () => {
    if (!tenant || linkLockRef.current) {
      return;
    }
    linkLockRef.current = true;
    setIsLinking(true);
    try {
      const { url } = await adminWoltService.createOnboardingLink(tenant.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (linkError: unknown) {
      notifications.show({ color: 'red', message: linkError instanceof Error ? linkError.message : 'יצירת קישור החיבור לוולט נכשלה', autoClose: 5000 });
    } finally {
      linkLockRef.current = false;
      setIsLinking(false);
    }
  };

  const syncMenu = async () => {
    if (!tenant || syncLockRef.current) {
      return;
    }
    syncLockRef.current = true;
    setIsSyncing(true);
    try {
      notifyWoltMenuSync(await adminWoltService.syncMenu(tenant.id));
      setRefreshTick((tick) => tick + 1);
    } catch (syncError: unknown) {
      notifications.show({ color: 'red', message: syncError instanceof Error ? syncError.message : 'סנכרון התפריט לוולט נכשל', autoClose: 5000 });
    } finally {
      syncLockRef.current = false;
      setIsSyncing(false);
    }
  };

  return { tenant, woltConnected, canEdit, products, isLoading, error, togglingIds, isSyncing, isLinking, toggleIncluded, syncMenu, connectToWolt };
}
